import _ from 'lodash';

window.onerror = undefined;

let epgInstance = null;

export const getEpgInstance = () => epgInstance;

export default class Core {
  constructor(props) {
    this.epg = null;

    this.props = props;
    this.prevChannelGenreId = null;
    this.lastFavoriteToggleData = null;
  }

  init = (epg) => {
    const {
      handleCurrentProgramChanged,
      handleActiveProgramChanged,
      handleCurrentProgramListUpdated,
      handleChannelsReady,
      handleProgramsReady,
      handleChannelSwitch,
      handleCurrentChannelChanged,
      handlePlayingProgramChanged,
      handleFavoriteToggle,
      handleSubscriptionInfoLoaded,
      handleAllChannelsButtonInfo,
    } = this.props;

    epgInstance = epg;
    const { Consts } = epgInstance;

    handleAllChannelsButtonInfo({
      allChannelsUrl: epg.getAllChannelsUrl(),
      showAllChannelsButton: epg.getShowAllChannelsButton(),
    });

    epgInstance.on('skin:nextChannelClick', () => this.switchToPrevNext('next'));
    epgInstance.on('skin:prevChannelClick', () => this.switchToPrevNext('prev'));

    // active program on CURRENT channel
    epgInstance.on(Consts.EVENT_CURRENT_PROGRAM_CHANGED, (event) => {
      handleCurrentProgramChanged(event);
    });

    // active program on OTHER (NOT CURRENT) channel
    epgInstance.on(Consts.EVENT_ACTIVE_PROGRAM_CHANGED, (event) => {
      handleActiveProgramChanged(event);
    });

    // updated programs list on CURRENT channel
    epgInstance.on(Consts.EVENT_CURRENT_PROGRAM_LIST_UPDATED, (event) => {
      handleCurrentProgramListUpdated(event);
    });

    // channels loaded, processed and current program found
    epgInstance.on(Consts.EVENT_CHANNELS_READY, () => {
      const orderedChannels = epgInstance.getOrderedChannels();
      const channels = epgInstance.getChannels();
      handleChannelsReady({ orderedChannels, channels });
    });

    // new programs loaded and processed
    epgInstance.on(Consts.EVENT_PROGRAMS_READY, (event) => {
      handleProgramsReady({ channels: event.data });
    });

    // event emitted from UI
    epgInstance.on(Consts.EVENT_CHANNEL_SWITCH, (event) => {
      handleChannelSwitch(event);
    });

    epgInstance.on(`property:${Consts.PROPERTY_CURRENT_CHANNEL}`, (event) => {
      handleCurrentChannelChanged(event);

      if (this.prevChannelGenreId === null && event.data) {
        if (event.data.isFavorite) {
          this.prevChannelGenreId = epgInstance.FAVORITE_GENRE_ID;
        } else if (event.data.isFeatured) {
          this.prevChannelGenreId = epgInstance.FEATURED_GENRE_ID;
        } else {
          this.prevChannelGenreId = event.data.originalGenre.id;
        }
      }
    });

    epgInstance.on('property:interactiveChannel:currentPlayingProgram', (event) => {
      handlePlayingProgramChanged(event)
    });

    epgInstance.on(Consts.EVENT_FAVORITE_TOGGLED, (event) => {
      this.lastFavoriteToggleData = event.data;
    });

    epgInstance.on('property:player:isFavorite', () => {
      if (this.lastFavoriteToggleData) {
        const {state, channel} = this.lastFavoriteToggleData;
        this.prevChannelGenreId = state ? epgInstance.FAVORITE_GENRE_ID : channel.originalGenre.id;
        handleFavoriteToggle(this.lastFavoriteToggleData)
      }
      this.lastFavoriteToggleData = null;
    });

    epgInstance.on(Consts.EVENT_SUBSCRIPTION_INFO_LOADED, (event) => {
      handleSubscriptionInfoLoaded(event.data);
    });
  };

  _getIsLinearHybridAdShowed = () => {
    let isLinearHybridAdShowed = false;
    const getIsLinearHybridAdShowed = epgInstance.get('getIsLinearHybridAdShowed');
    if (typeof getIsLinearHybridAdShowed === 'function') {
      isLinearHybridAdShowed = getIsLinearHybridAdShowed();
    }
    return isLinearHybridAdShowed;
  }

  switchChannel = ({ id, genreId, switchAfterClick }) => {
    const currentChannel = epgInstance.get('epg:currentChannel');

    if (currentChannel && currentChannel.id === id && this._getIsLinearHybridAdShowed()) {
      // we don't need side effects when user taps on the current channel and linear hybrid ad is being shown right now
      return;
    }

    if (currentChannel && currentChannel.id === id && genreId === this.prevChannelGenreId) {
      // if clicked on current channel
      if (this.getMessageModeData()) {
        // if has some message mode data
        // do nothing
      } else if (currentChannel.type === 'regularChannel') {
        if (epgInstance.get('state') === 2) {
          epgInstance.player.play();
        }
      } else {
        epgInstance.emit(`${currentChannel.type}:backOnAir`);
      }
    } else {
      const { setCurrentChannelAbsoluteId } = this.props;
      setCurrentChannelAbsoluteId({ id, genreId });
      const targetChannel = this.props.channelsMap[id];
      epgInstance.emit('epg:channelSwitch', targetChannel ? targetChannel : { id });

      if (currentChannel && currentChannel.id === id) {
        epgInstance.emit(`${currentChannel.type}:backOnAir`);
      }
    }
    if (switchAfterClick) {
      epgInstance.emit('epg:channelButtonClick');
    }
    this.prevChannelGenreId = genreId;
  };

  expandGenre = (genreId, state) => {
    epgInstance.emit('epg:genre:expandChange', { id: genreId,  state });
  }

  toggleExpand = (state) => {
    if (state && epgInstance.get('autoplay') === false) {
      epgInstance.emit('bigplay:click');
    }

    epgInstance.emit('epg:rightButton:stateChange', { currentState: state ? 3 : 2 });
  };

  emitAnimationEnd = () => {
    epgInstance.emit('epg:animationEnd');
  };

  switchToPrevNext = (type) => {
    let result;
    const { currentChannelAbsoluteId, channelsByGenres } = this.props;
    let genreIndex = _.findIndex(channelsByGenres, { id: currentChannelAbsoluteId.genreId });
    if (genreIndex !== -1) {
      let channelIndex;
      let genre = channelsByGenres[genreIndex];
      if (type === 'next') {
        channelIndex = getNextPrevObject(genre.channels, genre.channels.indexOf(currentChannelAbsoluteId.id), type);
        if (channelIndex === null) { // in this case we need to get first channel from next category
          genreIndex = getNextPrevObject(channelsByGenres, genreIndex, type);
          if (genreIndex === null) {
            genreIndex = 0;
          }
          genre = channelsByGenres[genreIndex];
          channelIndex = 0;
        }
      } else if (type === 'prev') {
        channelIndex = getNextPrevObject(genre.channels, genre.channels.indexOf(currentChannelAbsoluteId.id), type);
        if (channelIndex === null) { // in this case we need to get last channel from previous category
          genreIndex = getNextPrevObject(channelsByGenres, genreIndex, type);
          if (genreIndex === null) {
            genreIndex = channelsByGenres.length - 1;
          }
          genre = channelsByGenres[genreIndex];
          channelIndex = genre.channels.length - 1
        }
      }

      result = { genreId: genre.id, id: genre.channels[channelIndex] };
    }

    if (!result) {
      // if we couldn't find anything, get the first channel in first category
      result = { genreId: channelsByGenres[0].id, id: channelsByGenres[0].channels[0] }
    }


    this.switchChannel(result);

    function getNextPrevObject(array, index, nextOrPrev) {
      let result = null;
      if (index !== -1) {
        if (nextOrPrev === 'next') {
          result = index + 1 < array.length ? index + 1 : null;
        } else if (nextOrPrev === 'prev') {
          result = index - 1 >= 0 ? index - 1 : null;
        }
      }
      return result;
    }
  };

  switchProgram = (program, switchAfterClick) => {
    const currentChannel = epgInstance.get('epg:currentChannel');
    const playingProgram = epgInstance.get(epgInstance.Consts.PROPERTY_PLAYING_PROGRAM);

    if (playingProgram?.startTimestamp === program.startTimestamp && this._getIsLinearHybridAdShowed()) {
      // we don't need side effects when user taps on the current program and linear hybrid ad is being shown right now
      return;
    }

    if (currentChannel.type === 'dvrChannel' && program.state === 1) {
      epgInstance.emit(`${currentChannel.type}:backOnAir`);
    } else {
      let type;
      if (currentChannel.type === 'regularChannel') {
        type = 'vodChannel';
      } else {
        type = currentChannel.type;
      }
      program.clickedFromProgramsPanel = true;
      if(type === 'vodChannel' && program.state === 1) {
        epgInstance.emit(`${type}:backOnAir`);
      } else {
        epgInstance.emit(`${type}:playProgram`, program);
      }
    }

    if (switchAfterClick) {
      epgInstance.emit('epg:programButtonClick');
    }
    this.props.handleProgramSwitch(playingProgram);
  };

  findActive = (program) => {
    let currentProgram = null;
    if (epgInstance.get('interactiveChannel:currentPlayingProgram')) {
      currentProgram = epgInstance.get('interactiveChannel:currentPlayingProgram');
    } else if (program) {
      for (let i = 0; i < program.length; i++) {
        if (program[i].state === 1) {
          currentProgram = program[i];
        }
      }
    }

    return currentProgram;
  };

  loadExtendedProgram = () => {
    const channel = epgInstance.get('epg:currentChannel');
    if (channel && !channel.hasExtendedProgram) {
      epgInstance.emit('epg:needToLoadExtendedProgramList', { channel });
    }
  };

  translate = (key, defaultValue) => {
    return epgInstance.translate(key, defaultValue);
  };

  setProps = (props) => {
    this.props = props;
  };

  setSkinVisibilityCallBack = (cb) => {
    epgInstance.on('skin:skinVisibilityBeginChange', cb) // change visible controls
  }

  onRestrictedUIClick = (isSkipStubPresence) => {
    const onRestrictedClick = epgInstance.get('onRestrictedUIClick');
    if (typeof onRestrictedClick === 'function') {
      onRestrictedClick(isSkipStubPresence);
    }
  }

  getMessageModeData = () => {
    let messageModeData = null;
    const getMessageModeData = epgInstance.get('getMessageModeData');
    if (typeof getMessageModeData === 'function') {
      messageModeData = getMessageModeData();
    }
    return messageModeData;
  }
}
